import {
  animate,
  AnimatePresence,
  motion,
  useIsPresent,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react'
import {Dialog, Modal, ModalOverlay} from 'react-aria-components'

import useClientValue from '@/utils/hooks/useClientValue'

import style from './Sheet.module.scss'

// Wrap React Aria modal components so they support framer-motion values.
const MotionModal = motion.create(Modal)
const MotionModalOverlay = motion.create(ModalOverlay)

const inertiaTransition = {
  type: 'inertia' as const,
  bounceStiffness: 300,
  bounceDamping: 40,
  timeConstant: 300,
}

const staticTransition = {
  duration: 0.5,
  ease: [0.32, 0.72, 0, 1],
}

interface Props {
  isOpen: boolean
  onClose: MemoizedCallback<() => void>
  size: number
  onPresentChange?: MemoizedCallbackOrDispatch<boolean>
  mode: 'right' | 'bottom'
}

export default deepMemo(function Sheet({
  size,
  isOpen,
  onClose,
  children,
  onPresentChange,
  mode = 'right',
}: PropsWithChildren<Props>) {
  return (
    <AnimatePresence>
      {isOpen && (
        <CustomModal size={size} onClose={onClose} onPresentChange={onPresentChange} mode={mode}>
          {children}
        </CustomModal>
      )}
    </AnimatePresence>
  )
})

function CustomModal({
  onClose,
  children,
  size,
  onPresentChange,
  mode = 'right',
}: PropsWithChildren<Omit<Props, 'isOpen'>>) {
  const isPresent = useIsPresent()

  setTimeout(() => {
    onPresentChange?.(isPresent)
  }, 0)

  const axis = (() => {
    switch (mode) {
      case 'right':
        return 'x'
      case 'bottom':
        return 'y'
    }
  })()

  const prop = (() => {
    switch (mode) {
      case 'right':
        return 'width'
      case 'bottom':
        return 'height'
    }
  })()

  const prop2 = (() => {
    switch (mode) {
      case 'right':
        return 'left'
      case 'bottom':
        return 'top'
    }
  })()

  const innerSize = useClientValue(() => (axis === 'x' ? window.innerWidth : window.innerHeight), 0)

  const motionValue = useMotionValue(size)
  const bgOpacity = useTransform(motionValue, [0, size], [0.3, 0])
  const bg = useMotionTemplate`rgba(0, 0, 0, ${bgOpacity})`

  return (
    <MotionModalOverlay
      // Force the modal to be open when AnimatePresence renders it.
      isOpen
      onOpenChange={onClose}
      className={style.Overlay}
      style={{backgroundColor: bg}}
      isDismissable
    >
      <MotionModal
        className={clsx(
          style.Modal,
          (() => {
            switch (mode) {
              case 'right':
                return style.Right
              case 'bottom':
                return style.Bottom
            }
          })(),
        )}
        initial={{[axis]: size}}
        animate={{[axis]: 0}}
        exit={{[axis]: size}}
        transition={staticTransition}
        style={{
          ...(mode === 'right' ? {top: 0} : {}),
          [axis]: motionValue,
          [prop]: size,
          [prop2]: innerSize - size,
          // Extra padding at the bottom to account for rubber band drag?
          // paddingRight: window.screen.width,
        }}
        drag={axis}
        dragElastic={{[mode]: 0}}
        dragConstraints={{[prop2]: 0}}
        onDragEnd={(e, {offset, velocity}) => {
          if (offset[axis] > innerSize * 0.75 || velocity[axis] > 10) {
            onClose()
          } else {
            void animate(motionValue, 0, {...inertiaTransition, min: 0, max: 0})
          }
        }}
      >
        {mode === 'bottom' && (
          <div className={style.DragAffordance}>
            <div />
          </div>
        )}
        <Dialog className={style.Dialog}>{children}</Dialog>
      </MotionModal>
    </MotionModalOverlay>
  )
}
